import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, TextField, MenuItem, RadioGroup, Radio, FormControlLabel, Button, ToggleButtonGroup, ToggleButton, IconButton, Typography, Divider } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { generateCheckSchedule } from './schedule';
import { parseMoney } from './money';

export default function CheckEditor({ date, totalCents, accounts, checks, setChecks, onPhotos, generateRef, onCanGenerateChange }) {
  const { t } = useTranslation();
  const operating = accounts.filter(a => a.role === 'operating');
  const [count, setCount] = useState('1');
  const [firstNumber, setFirstNumber] = useState('');
  const [firstDate, setFirstDate] = useState(date);
  const [how, setHow] = useState('monthly');
  const [amountEach, setAmountEach] = useState('');
  const [accountId, setAccountId] = useState(operating[0]?.id ?? null);

  const generate = () => {
    const n = Number(count);
    const each = parseMoney(amountEach);
    const list = generateCheckSchedule({ count: n, firstCheckNumber: firstNumber, firstDate, cadence: how, totalCents, amountCents: each ?? undefined })
      .map(c => ({ checkNumber: c.checkNumber, dueDate: c.dueDate, amountText: (c.amountCents / 100).toFixed(2), bankAccountId: accountId, low: new Set() }));
    setChecks(list);
    return list;
  };
  const canGenerate = Boolean(firstNumber && firstDate && Number(count) && accountId && totalCents);
  useEffect(() => {
    if (generateRef) generateRef.current = canGenerate ? generate : null;
    onCanGenerateChange?.(canGenerate);
  });
  const edit = (i, patch) => setChecks(checks.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  return (
    <Stack spacing={2}>
      {onPhotos}
      <Typography variant='subtitle1'>{t('calendar.checks.generator')}</Typography>
      <TextField label={t('calendar.checks.count')} value={count} onChange={e => setCount(e.target.value.replace(/\D/g, ''))} inputProps={{ inputMode: 'numeric' }} />
      <TextField label={t('calendar.checks.firstNumber')} value={firstNumber} error={!firstNumber} onChange={e => setFirstNumber(e.target.value.replace(/\D/g, ''))} inputProps={{ inputMode: 'numeric' }} />
      <TextField label={t('calendar.checks.firstDate')} type='date' value={firstDate} error={!firstDate} onChange={e => setFirstDate(e.target.value)} InputLabelProps={{ shrink: true }} />
      <ToggleButtonGroup exclusive size='small' value={how} onChange={(e, v) => v && setHow(v)}>
        <ToggleButton value='monthly'>{t('calendar.checks.monthly')}</ToggleButton>
        <ToggleButton value='biweekly'>{t('calendar.checks.biweekly')}</ToggleButton>
        <ToggleButton value='weekly'>{t('calendar.checks.weekly')}</ToggleButton>
      </ToggleButtonGroup>
      <TextField label={t('calendar.checks.amountEach')} value={amountEach} onChange={e => setAmountEach(e.target.value)} inputProps={{ inputMode: 'decimal' }} />
      <RadioGroup value={accountId ?? ''} onChange={e => setAccountId(Number(e.target.value))}>
        {operating.map(a => <FormControlLabel key={a.id} value={a.id} control={<Radio />} label={a.nickname} />)}
      </RadioGroup>
      <Button variant='outlined' disabled={!canGenerate} onClick={generate}>{t('calendar.checks.generator')}</Button>
      <Stack divider={<Divider />}>
        {checks.map((c, i) => (
          <Stack key={i} data-testid='check-row' direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ py: 1 }}>
            <TextField size='small' label={t('calendar.checks.number')} value={c.checkNumber} error={c.low.has('check_number') || !/^\d+$/.test(c.checkNumber)} onChange={e => edit(i, { checkNumber: e.target.value.replace(/\D/g, '') })} />
            <TextField size='small' type='date' label={t('calendar.checks.date')} value={c.dueDate} error={c.low.has('date') || !/^\d{4}-\d{2}-\d{2}$/.test(c.dueDate)} InputLabelProps={{ shrink: true }} onChange={e => edit(i, { dueDate: e.target.value })} />
            <TextField size='small' label={t('calendar.checks.amount')} value={c.amountText} error={c.low.has('amount_cents') || parseMoney(c.amountText) == null} onChange={e => edit(i, { amountText: e.target.value })} />
            <TextField select size='small' label={t('calendar.checks.account')} value={c.bankAccountId ?? ''} error={!c.bankAccountId}
              onChange={e => edit(i, { bankAccountId: Number(e.target.value) })}>
              {operating.map(a => <MenuItem key={a.id} value={a.id}>{a.nickname}</MenuItem>)}
            </TextField>
            <IconButton aria-label={t('common.delete')} onClick={() => setChecks(checks.filter((_, j) => j !== i))}><DeleteIcon /></IconButton>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}
